import { computed, provide } from 'vue'
import { useBreadcrumb, useRoute, useStore, useVuelidate } from '@/plugins'
import { cachedObject } from 'best-modules/utils'
import { setSnackbar } from 'best-modules/plugins'
import { Task } from '@/utils/types/task'
import { notifyValidation } from '@/utils/helpers'
import { required } from '@vuelidate/validators'
import { useCan } from '@/composables/useCan'
import { taskSingle, taskUpdate } from '@/requests/task'
import { omit } from 'lodash'

export function useEditTask() {
  const store = useStore()
  const route = useRoute()
  const breadcrumb = useBreadcrumb()
  const { can } = useCan()
  const isAbsence = [
    'absence-single-task',
    'absence-outgoing-tasks',
    'absence-incoming-tasks',
  ].includes(<string>route.name)
  const task = cachedObject<Task>({} as Task)
  task.$setIgnore(
    'created_at',
    'updated_at',
    'comments',
    'statusId',
    'status',
    'controlUsers',
    'observers',
    'executor',
    'executorId',
    'deadline'
  )

  const taskType = computed(() => {
    return {
      execution: task.typeId === 1,
      familiarization: task.typeId === 2,
      errand: task.typeId === 3,
    }
  })

  const rules = {
    authorId: !taskType.value.execution ? { required } : {},
    topic: taskType.value.execution ? { required } : {},
    description: taskType.value.errand ? {} : { required },
    executorId: !taskType.value.familiarization ? { required } : {},
    deadline: { required },
    attachment: {},
  }
  const v$ = useVuelidate(rules, task.$object)

  const userId = computed(() => store.state.user.userData?.id)
  const isAuthor = computed(() => userId.value === task.authorId)

  const disabled = computed(() => {
    return [2, 3, 4, 5].includes(task.statusId) || !can('tasks.update')
  })

  const getTask = () => {
    return taskSingle(route.params.id, +route.params?.absenceId)
      .then(res => {
        task.$set(res)
        return res
      })
      .then(res => {
        const isIncoming = res.executorId === userId.value
        let taskDirection = 'outgoing'
        if (isIncoming) {
          taskDirection = isAbsence ? 'absence-incoming' : 'incoming'
        } else if (isAbsence) {
          taskDirection = 'absence-outgoing'
        }
        breadcrumb.set([
          {
            title: `${
              ['outgoing', 'absence-outgoing'].includes(taskDirection)
                ? 'Вихідні'
                : 'Вхідні'
            } завдання`,
            to: { name: `${taskDirection}-tasks` },
            index: 0,
          },
          { title: res.topic, index: 1 },
        ])
      })
  }

  const submit = (extraData: Record<string, unknown> = {}) => {
    v$.value.$validate()
    if (!v$.value.$error) {
      return taskUpdate(task.id, {
        ...omit(task.$object, ['attachment']),
        ...extraData,
      }).then(res => {
        task.$set(res)
        const entityName = taskType.value.errand ? 'Доручення' : 'Завдання'
        setSnackbar({ text: `${entityName} оновлено` })
        return res
      })
    } else {
      notifyValidation(v$.value)
    }
  }

  const cancel = () => {
    return task.$reset()
  }

  provide('taskType', taskType)

  return {
    v$,
    task,
    taskType,
    submit,
    cancel,
    getTask,
    disabled,
    isAuthor,
  }
}
